
import React, { useState } from 'react';
import { ChevronLeft, Zap, ShieldAlert, UserPlus, Settings, ExternalLink, Loader2 } from 'lucide-react';
import { AnimatedPricingPage } from './ui/animated-pricing-page.tsx';
import { createCheckoutSession, openCustomerPortal } from '../services/stripeService.ts';

interface StoreViewProps {
  user: any;
  userTier: string;
  setActiveView: (view: any) => void;
  setAuthView: (view: any) => void;
  isGuest: boolean;
}

export default function StoreView({ user, userTier, setActiveView, setAuthView, isGuest }: StoreViewProps) {
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const isPaidUser = userTier !== 'guest' && userTier !== 'registered';

  const handleManageSubscription = async () => {
    if (!user || isPortalLoading) return;
    setIsPortalLoading(true);
    try {
      await openCustomerPortal(user.id);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPortalLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in slide-in-from-bottom-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl hover:scale-110 active:scale-90 transition-transform shadow-lg">
            <ChevronLeft size={24}/>
          </button>
          <div className="flex flex-col">
            <h2 className="text-5xl font-bungee text-indigo-400 uppercase tracking-tighter">SIGMA STORE</h2>
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Current Rank: {userTier.toUpperCase()}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isPaidUser && !isGuest && (
            <button 
              onClick={handleManageSubscription}
              disabled={isPortalLoading}
              className="flex items-center gap-2 px-6 py-3 bg-zinc-800 border-2 border-zinc-700 rounded-2xl font-bungee text-[10px] text-white hover:bg-zinc-700 hover:border-indigo-500/50 transition-all shadow-xl disabled:opacity-50"
            >
              {isPortalLoading ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />}
              Manage Subscription
              <ExternalLink size={12} className="opacity-50" />
            </button>
          )}

          {isGuest && (
            <div className="bg-red-500/10 border-2 border-red-500/20 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
              <ShieldAlert className="text-red-500" size={24} />
              <div className="flex flex-col">
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Guest Account Active</span>
                <button 
                  onClick={() => setAuthView('signup')}
                  className="text-[8px] font-bold text-red-400 uppercase tracking-tighter hover:underline flex items-center gap-1"
                >
                  <UserPlus size={10} /> Create permanent ID to keep upgrades
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Animated Pricing Section */}
      <div className={isGuest ? 'opacity-50 pointer-events-none' : ''}>
        <AnimatedPricingPage user={user} userTier={userTier} />
      </div>

      {isGuest && (
        <div className="max-w-xl mx-auto text-center space-y-6 py-12">
           <h3 className="font-bungee text-2xl text-white uppercase">Identity Required</h3>
           <p className="text-sm font-bold text-zinc-500 uppercase tracking-widest leading-relaxed">
             Upgrades cannot be applied to guest accounts. Sign up to secure your high-aura rank forever.
           </p>
           <button 
             onClick={() => setAuthView('signup')}
             className="px-10 py-6 bg-indigo-600 text-white font-bungee text-2xl rounded-2xl shadow-[0_8px_0_0_#4338ca] hover:bg-indigo-500 hover:scale-105 active:translate-y-2 active:shadow-none transition-all"
           >
             JOIN THE TRIBE
           </button>
        </div>
      )}

      {/* Refill Section */}
      {!isGuest && (
        <div className="max-w-2xl mx-auto bg-yellow-400/5 border-2 border-yellow-400/20 rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center justify-between gap-8 mt-12 shadow-2xl">
          <div className="flex items-center gap-6">
            <div className="p-5 bg-yellow-400 rounded-3xl text-black shadow-[0_0_20px_rgba(250,196,21,0.3)]">
              <Zap size={32} />
            </div>
            <div className="space-y-1">
              <h3 className="font-bungee text-2xl text-white uppercase leading-none">Emergency Rizz</h3>
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Instant 10-Blast Refill</p>
            </div>
          </div>
          <button 
            onClick={() => createCheckoutSession('refill', user.id, user.email)}
            className="w-full md:w-auto px-10 py-5 bg-yellow-400 text-black font-bungee text-xl rounded-2xl shadow-[0_8px_0_0_#ca8a04] hover:bg-yellow-300 hover:scale-105 active:translate-y-2 active:shadow-none transition-all flex items-center justify-center gap-2"
          >
            $0.99 BLOWOUT
          </button>
        </div>
      )}

      <style>{`
        .pricing-card-gold.animate-blast-glow {
          animation: rainbow-pulse 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
